import { Injectable } from '@angular/core';

import { isEmpty } from 'lodash-es';
import { QueryHandleQuotes } from '../../../advanced-search/filters/base/entities/query-part-handle-quotes';
import { QueryPartStrategy } from '../../../advanced-search/filters/base/entities/query-part-strategy';
import { QueryPartData } from '../../../advanced-search/filters/base/entities/query-part-data.entity';
import { QueryPart } from '../../../advanced-search/filters/base/entities/query-part.class';
import { SearchMode } from '../../../advanced-search/filters/entities/search-mode.entity';
import { rangeValueToString } from '../../../saved-queries/helpers/convert-structured-query.helper';
import { IsDateRangeQuery, IsLaxDateRangeQuery } from '../../dates/helpers/date-range-query-helper';
import { LogicalOperand, Operand } from '../../query/entities/operand.entity';
import {
    AdvancedSearch,
    AdvancedSearchGroup,
    AdvancedSearchTerm,
    Bucket,
    Facet
} from '../../query/entities/structured-query.entity';
import { ApiQuery, OperandAsString } from '../entities/api-query.entity';
import { SanitizeFieldSearchValue } from './field-search-value.helper';
import { IsGeocoordinateRangeQuery } from './geocoordinate-range-query-helper';

type SanitizeValueFn = (value: string, handleQuotes?: QueryHandleQuotes) => string;

@Injectable({ providedIn: 'root' })
export class ApiQueryBuilder {
    private static readonly VALUES_TO_ESCAPE: string[] = [
        '+',
        '-',
        '&',
        '|',
        '!',
        '^',
        `"`,
        '~',
        ':',
        '\\',
        '/',
        '(',
        ')',
        '[',
        ']',
        '{',
        '}',
        ' ',

        // TODO: move this back to wildcards at some point
        '?'
    ];

    private static readonly VALUES_TO_ESCAPE_REGEX = new RegExp(`(${ApiQueryBuilder.getRegexBody()})`, 'gi');

    protected static readonly WILDCARD_VALUES: string[] = ['*'];

    public static queryToLuceneQuery(query: ApiQuery): string {
        const q: string = this.buildGlobalSearchQuery(query).trim();

        const advSearchQuery: string = this.buildAdvancedSearchQuery(query).trim();

        const facetQuery: string = this.buildFacetQuery(query).trim();

        const userQuery = `${q} ${advSearchQuery} ${facetQuery}`.trim();

        const restrictionQuery: string = query.restriction
            ? OperandAsString(query.restriction, this.getQueryPartAsLuceneQuery.bind(this))
            : '';

        const wrappedRestrictionQuery: string =
            userQuery && restrictionQuery ? `+(${restrictionQuery})` : restrictionQuery;

        return [userQuery, wrappedRestrictionQuery]
            .filter(str => str.length > 0)
            .join(' ')
            .trim();
    }

    public static buildGlobalSearchQuery(query: ApiQuery): string {
        const globalValues = query.structuredQuery.Global;
        return this.getFieldLuceneQuery(SearchMode.Global, globalValues, Operand.Include);
    }

    public static buildSemanticSearchQuery(query: ApiQuery): string {
        const semanticQuery: string = query.structuredQuery.Semantic;
        return semanticQuery;
    }

    public static buildFullTextSearchQuery(query: ApiQuery): string {
        return query.structuredQuery.FullText;
    }

    // #region Advanced Search Query
    public static buildAdvancedSearchQuery(query: ApiQuery): string {
        const advancedSearch: AdvancedSearch = query.structuredQuery.AdvancedSearch;
        const advGroupQueries: string[] = advancedSearch.Groups.map(group =>
            this.buildAdvancedSearchGroupQuery(group)
        ).filter(groupQuery => groupQuery);

        if (advGroupQueries.length === 0) {
            return '';
        }

        const logicalOperand: LogicalOperand = advancedSearch.LogicalOperand;

        const advancedSearchQuery: string = advGroupQueries
            .map(advGroupQuery => {
                if (LogicalOperand.Or === logicalOperand) {
                    return `(${advGroupQuery})`;
                } else if (LogicalOperand.And === logicalOperand) {
                    return `+(${advGroupQuery})`;
                }

                // Operand isn't supported
                return '';
            })
            .join(' ');

        return `+(${advancedSearchQuery})`;
    }

    private static buildAdvancedSearchGroupQuery(advancedSearchGroup: AdvancedSearchGroup): string {
        const logicalOperand: LogicalOperand = advancedSearchGroup.LogicalOperand;
        const termQueries: string[] = (advancedSearchGroup.Terms ?? [])
            .flatMap(term => {
                if (term.RangeValues) {
                    return term.RangeValues.map(value => {
                        return this.buildAdvancedSearchTermQuery({
                            DottedKey: term.DottedKey,
                            Operand: term.Operand,
                            Values: [rangeValueToString(value)]
                        });
                    });
                }

                return (term.Values ?? []).map(value =>
                    // Every value needs to be separated to ensure backwards compatibility with older queries
                    this.buildAdvancedSearchTermQuery({
                        ...term,
                        Values: [value]
                    })
                );
            })
            .filter(groupQuery => groupQuery);

        if (termQueries.length === 0) {
            return '';
        }

        if (termQueries.length === 1) {
            // Doesn't matter if only term in group
            return termQueries[0];
        }

        return termQueries
            .map(termQuery => {
                if (LogicalOperand.Or == logicalOperand) {
                    // (+ImageQuality:High) (+Keyword:test)
                    return `(${termQuery})`;
                } else if (LogicalOperand.And == logicalOperand) {
                    // +ImageQuality:High +Keyword:test
                    return termQuery;
                }

                // Operand isn't supported
                return '';
            })
            .join(' ');
    }

    private static buildAdvancedSearchTermQuery(term: AdvancedSearchTerm): string {
        const dottedKey: string = term.DottedKey;
        const handleQuotes: QueryHandleQuotes = QueryHandleQuotes.Escape;

        const values: string[] = (term.Values ?? [])
            .map(value => {
                // MED-4624: Little hacky way, but for now we manually detect if a user wants to perform a specific search by surrounding the value with quotes
                // To be replaced in a more solid way and introduce 'precise search'/'wildcard search'/... in the advanced search dialog
                if (value.startsWith('"') && value.endsWith('"')) {
                    const withoutQuotes = value.substring(1, value.length - 1);

                    return `"${this.sanitizeValue(withoutQuotes, handleQuotes)}"`;
                }
                return this.sanitizeValue(value, handleQuotes);
            })
            .filter(term => term.length > 0);

        if (values.length === 0) {
            return '';
        }

        const operand: Operand = term.Operand;

        if (Operand.Include == operand) {
            return `+${dottedKey}:${values[0]}`;
        } else if (Operand.Exclude == operand) {
            return `-${dottedKey}:${values[0]}`;
        } else {
            return `${dottedKey}:${values[0]}`;
        }
    }
    // #endregion

    // #region Facet Query
    public static buildFacetQuery(query: ApiQuery): string {
        const facets: Facet[] = query.structuredQuery.Facets;

        const facetQueries: string[] = facets.flatMap(facet => {
            const buckets =
                facet.Buckets && facet.Buckets.length > 0
                    ? facet.Buckets
                    : (facet.RangeBuckets?.map(bucket => ({
                          Operand: bucket.Operand,
                          Value: rangeValueToString(bucket.RangeValue)
                      })) ?? []);

            // Assuming only excluding 'no value' buckets for now
            const queryStrings = buckets.map(bucket => {
                const isMissingValue: boolean = bucket.Operand === Operand.Exclude;

                // MED-5510 + MED-5636: facet values should be escaped unless they are exclusively wildcards
                const handleQuotes: QueryHandleQuotes = isMissingValue
                    ? QueryHandleQuotes.Escape
                    : QueryHandleQuotes.UseQuotes;

                // MED-5833: Catch 'missing' value
                // Value will be 'No {field}' since that is what API returns
                // Since we don't work with other Excluding facets anyway, we always default to '*'
                bucket = isMissingValue ? { ...bucket, Value: '*' } : bucket;

                return this.getFacetBucketAsQuery(facet.DottedKey, bucket, handleQuotes);
            });

            const preparedQueryStrings =
                facet.LogicalOperand === LogicalOperand.Or && queryStrings.length > 1
                    ? queryStrings.map(qs => `(${qs})`)
                    : queryStrings;

            // OR logic between buckets
            // Expected behavior:
            // For OR facets: ((+field:value1) (+field:value2))
            // For AND facets: +field:value1 +field:value2
            switch (facet.LogicalOperand) {
                case LogicalOperand.Or:
                    return preparedQueryStrings.length > 1
                        ? `+(${preparedQueryStrings.join(' ')})`
                        : preparedQueryStrings;
                case LogicalOperand.And:
                    return preparedQueryStrings.join(' ');
                default:
                    console.warn('Unknown logicalOperand for facet, defaulting to And');
                    return '';
            }
        });

        return facetQueries.join(' ');
    }

    private static getFacetBucketAsQuery(dottedKey: string, bucket: Bucket, handleQuotes: QueryHandleQuotes): string {
        const prefix = this.getBucketPrefix(bucket);

        const sanitizedValue = this.sanitizeValue(bucket.Value, handleQuotes);

        const query = `${prefix}${dottedKey}:${sanitizedValue}`;

        return query;
    }

    private static getBucketPrefix(bucket: Bucket) {
        switch (bucket.Operand) {
            case Operand.Include:
            case Operand.Should:
                return '+';
            case Operand.Exclude:
                return '-';
            default:
                console.warn('Unknown operand for bucket, defaulting to include (+)');
                return '+';
        }
    }
    // #endregion

    // #region Convert Queryparts
    /**
     * Converts the column filters (`cf`) into the Lucene clause the search API applies for them.
     * Both the table's own search and the facet request build their clause here, so the two always
     * count the same population.
     */
    public static columnFiltersToLuceneQuery(columnFilters: QueryPartData[] | undefined): string {
        const columnFilterQueries: string[] = (columnFilters ?? []).map(columnFilter =>
            ApiQueryBuilder.getQueryPartAsLuceneQuery(
                new QueryPart({
                    ...columnFilter,
                    // Date-range filters carry a Lucene `[start TO end]` value that must reach the API
                    // verbatim; only text filters get the case-insensitive contains wildcards.
                    values: columnFilter.values.map(value =>
                        IsDateRangeQuery(value) ? value : this.toContainsValue(value.toLowerCase())
                    )
                }),
                // Quote handling is this sanitiser's own business, so the query part carries none.
                SanitizeFieldSearchValue
            )
        );

        return columnFilterQueries.join(' ');
    }

    private static toContainsValue(value: string): string {
        return this.WILDCARD_VALUES.some(wildcard => value.includes(wildcard)) ? value : `*${value}*`;
    }

    public static getQueryPartAsLuceneQuery(
        query: QueryPart,
        sanitizeValue: SanitizeValueFn = (value, handleQuotes) => this.sanitizeValue(value, handleQuotes)
    ): string {
        if (!query.hasValues()) return '';

        const isGlobal = query.field === SearchMode.Global;

        if (isGlobal) query = this.transformGlobalQuery(query);

        const fieldPart = isGlobal ? '' : `${query.field}:`;
        const prefix = this.getQueryPartPrefix(query, isGlobal);
        const searchValues = query.values.map(
            value => `${this.getSearchValuesPrefix(query)}${sanitizeValue(value, query.handleQuotes)}`
        );

        const searchValue = searchValues.length === 1 ? searchValues[0] : `(${searchValues.join(' ')})`;

        return `${prefix}${fieldPart}${searchValue}`;
    }

    private static transformGlobalQuery(query: QueryPart) {
        const hasWildCard = query.values.some(value => this.WILDCARD_VALUES.some(wildcard => value.includes(wildcard)));

        return new QueryPart({
            ...query,
            handleQuotes: hasWildCard ? QueryHandleQuotes.Escape : QueryHandleQuotes.UseQuotes
        });
    }

    private static getQueryPartPrefix(query: QueryPart, isGlobal: boolean) {
        return query.strategy === QueryPartStrategy.MatchNone && (isGlobal || query.values.length === 1) ? '-' : '+';
    }

    private static getSearchValuesPrefix(query: QueryPart) {
        if (query.values.length <= 1) return '';

        if (query.strategy === QueryPartStrategy.MatchAll) return '+';
        if (query.strategy === QueryPartStrategy.MatchNone) return '-';
        return '';
    }

    // #endregion

    // #region Convert fields
    private static getFieldLuceneQuery(field: string, values: string[], operand: Operand): string {
        const nonEmptyValues: string[] = values.filter(value => !isEmpty(value));

        if (nonEmptyValues.length === 0) return '';

        const isGlobal = field === SearchMode.Global;

        let handleQuotes: QueryHandleQuotes = QueryHandleQuotes.Escape;

        if (isGlobal) {
            const hasWildCard = nonEmptyValues.some(value =>
                this.WILDCARD_VALUES.some(wildcard => value.includes(wildcard))
            );
            handleQuotes = hasWildCard ? QueryHandleQuotes.Escape : QueryHandleQuotes.UseQuotes;
        }

        const fieldPart = isGlobal ? '' : `${field}:`;

        const prefix = this.getPrefix(nonEmptyValues, operand, isGlobal);
        const searchValues = nonEmptyValues.map(
            value =>
                `${this.getPartSearchValuesPrefix(nonEmptyValues, operand)}${this.sanitizeValue(value, handleQuotes)}`
        );

        const searchValue = searchValues.length === 1 ? searchValues[0] : `(${searchValues.join(' ')})`;

        return `${prefix}${fieldPart}${searchValue}`;
    }

    private static getPrefix(values: string[], operand: Operand, isGlobal: boolean) {
        return operand === Operand.Exclude && (isGlobal || values.values.length === 1) ? '-' : '+';
    }

    private static getPartSearchValuesPrefix(values: string[], operand: Operand) {
        if (values.length <= 1) return '';

        if (operand === Operand.Include) return '+';
        if (operand === Operand.Exclude) return '-';
        return '';
    }
    // #endregion

    // #region Utils
    public static sanitizeValue(value: string, handleQuotes: QueryHandleQuotes = QueryHandleQuotes.Escape): string {
        if (IsLaxDateRangeQuery(value) || IsGeocoordinateRangeQuery(value)) {
            return value;
        }

        if (handleQuotes === QueryHandleQuotes.UseQuotes) {
            const escapedQuotes = value.replace(/\\/g, `\\\\`).replace(/"/g, `\\"`);
            return `"${escapedQuotes}"`;
        } else {
            return this.escapeValues(value);
        }
    }

    protected static escapeValues(unescapedValue: string): string {
        return unescapedValue.replace(ApiQueryBuilder.VALUES_TO_ESCAPE_REGEX, '\\$1');
    }

    private static getRegexBody() {
        return ApiQueryBuilder.VALUES_TO_ESCAPE.map(value => `\\${value}`).join('|');
    }
    // #endregion
}
